/**
 * IndexedDB wrappers for the kiosk PWA.
 *
 * Three object stores:
 *   - scan-queue: pending scans waiting to be POSTed (offline + retry buffer)
 *   - cache:      barcode → fullName lookup for optimistic overlays
 *   - meta:       generic key/value bucket (last-sync, updatePendingSince, …)
 *
 * The DB lives only in the kiosk; the teacher dashboard does not touch it.
 * All helpers are typed and resilient to a missing IndexedDB (SSR / tests
 * that haven't installed fake-indexeddb).
 */

import { openDB, type IDBPDatabase } from 'idb';

export const DB_NAME = 'pa-kiosk';
/**
 * v1 → v2: add `nextAttemptAt` (ms since epoch) to each scan-queue item so
 * the drain loop can honor per-item exponential backoff. Old items are
 * migrated with `nextAttemptAt = Date.now()` so they remain eligible on the
 * next drain — same behavior as a fresh enqueue.
 */
export const DB_VERSION = 2;

export const SCAN_QUEUE_STORE = 'scan-queue';
export const CACHE_STORE = 'cache';
export const META_STORE = 'meta';

export interface ScanQueueItem {
  id?: number;
  barcode: string;
  /** ms since epoch — the original scan time, NOT the submit time. */
  timestamp: number;
  retryCount: number;
  /**
   * ms since epoch — earliest time the drain may POST this item again.
   * Fresh enqueues set this to `Date.now()` (immediate). After a transient
   * failure (5xx, network, 429) the drain bumps this forward by either the
   * server-supplied `Retry-After` or the local exponential-backoff value.
   */
  nextAttemptAt: number;
}

export interface CacheEntry {
  barcode: string;
  fullName: string;
}

export interface MetaFailedEntry {
  reason: string;
  at: number;
  barcode?: string;
  retryCount?: number;
}

export interface MetaShape {
  /** Earliest timestamp at which a Service-Worker update was detected. */
  updatePendingSince?: number;
  /** Last time the queue was successfully drained (full or partial). */
  lastSyncAt?: number;
  /** Last time the cache was refreshed. */
  cacheRefreshedAt?: number;
  /** Append-only log of dropped queue items. */
  failed?: MetaFailedEntry[];
}

let dbPromise: Promise<IDBPDatabase> | null = null;

function hasIndexedDB(): boolean {
  return typeof indexedDB !== 'undefined';
}

export function getDb(): Promise<IDBPDatabase> {
  if (!hasIndexedDB()) {
    return Promise.reject(new Error('IndexedDB not available'));
  }
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      async upgrade(db, oldVersion, _newVersion, transaction) {
        if (!db.objectStoreNames.contains(SCAN_QUEUE_STORE)) {
          db.createObjectStore(SCAN_QUEUE_STORE, {
            keyPath: 'id',
            autoIncrement: true,
          });
        }
        if (!db.objectStoreNames.contains(CACHE_STORE)) {
          db.createObjectStore(CACHE_STORE, { keyPath: 'barcode' });
        }
        if (!db.objectStoreNames.contains(META_STORE)) {
          db.createObjectStore(META_STORE);
        }
        // v1 → v2: backfill nextAttemptAt on existing scan-queue rows. Old
        // items become drain-eligible at the upgrade moment, which is the
        // same semantics they had before this field existed. Awaiting the
        // cursor walk inside this async upgrade callback keeps the
        // versionchange transaction alive — idb won't commit until the
        // returned promise settles.
        if (oldVersion < 2) {
          const now = Date.now();
          const store = transaction.objectStore(SCAN_QUEUE_STORE);
          let cursor = await store.openCursor();
          while (cursor) {
            const item = cursor.value as Partial<ScanQueueItem>;
            if (item.nextAttemptAt === undefined) {
              await cursor.update({ ...item, nextAttemptAt: now });
            }
            cursor = await cursor.continue();
          }
        }
      },
    });
  }
  return dbPromise;
}

/** Forget the cached connection — used by tests + after a fatal error. */
export function resetDbForTests(): void {
  if (dbPromise) {
    void dbPromise.then((db) => db.close()).catch(() => {});
  }
  dbPromise = null;
}

// -------- scan-queue --------

/**
 * Insert a scan into the queue. `nextAttemptAt` defaults to `Date.now()` so
 * callers that don't care about scheduling (e.g. test fixtures) keep the
 * v1 API shape. Production callsites should set it explicitly.
 */
export async function enqueueScan(
  item: Omit<ScanQueueItem, 'id' | 'nextAttemptAt'> & { nextAttemptAt?: number },
): Promise<number> {
  const db = await getDb();
  const full: Omit<ScanQueueItem, 'id'> = {
    ...item,
    nextAttemptAt: item.nextAttemptAt ?? Date.now(),
  };
  return (await db.add(SCAN_QUEUE_STORE, full)) as number;
}

export async function listScanQueue(): Promise<ScanQueueItem[]> {
  const db = await getDb();
  return (await db.getAll(SCAN_QUEUE_STORE)) as ScanQueueItem[];
}

export async function countScanQueue(): Promise<number> {
  const db = await getDb();
  return db.count(SCAN_QUEUE_STORE);
}

export async function removeScanQueueItem(id: number): Promise<void> {
  const db = await getDb();
  await db.delete(SCAN_QUEUE_STORE, id);
}

export async function updateScanQueueItem(item: ScanQueueItem): Promise<void> {
  if (item.id === undefined) {
    throw new Error('updateScanQueueItem requires an id');
  }
  const db = await getDb();
  await db.put(SCAN_QUEUE_STORE, item);
}

export async function clearScanQueue(): Promise<void> {
  const db = await getDb();
  await db.clear(SCAN_QUEUE_STORE);
}

// -------- cache --------

export async function putCacheEntry(entry: CacheEntry): Promise<void> {
  const db = await getDb();
  await db.put(CACHE_STORE, entry);
}

export async function bulkPutCacheEntries(entries: CacheEntry[]): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(CACHE_STORE, 'readwrite');
  for (const e of entries) {
    void tx.store.put(e);
  }
  await tx.done;
}

export async function getCacheEntry(barcode: string): Promise<CacheEntry | null> {
  const db = await getDb();
  const result = (await db.get(CACHE_STORE, barcode)) as CacheEntry | undefined;
  return result ?? null;
}

export async function clearCache(): Promise<void> {
  const db = await getDb();
  await db.clear(CACHE_STORE);
}

export async function countCache(): Promise<number> {
  const db = await getDb();
  return db.count(CACHE_STORE);
}

// -------- meta --------

export async function getMeta<K extends keyof MetaShape>(
  key: K,
): Promise<MetaShape[K] | undefined> {
  const db = await getDb();
  return (await db.get(META_STORE, key)) as MetaShape[K] | undefined;
}

export async function setMeta<K extends keyof MetaShape>(
  key: K,
  value: MetaShape[K],
): Promise<void> {
  const db = await getDb();
  await db.put(META_STORE, value, key);
}

export async function deleteMeta<K extends keyof MetaShape>(key: K): Promise<void> {
  const db = await getDb();
  await db.delete(META_STORE, key);
}

export async function appendFailed(entry: MetaFailedEntry): Promise<void> {
  const existing = (await getMeta('failed')) ?? [];
  await setMeta('failed', [...existing, entry]);
}
